import React from "react";

interface InterviewOnboardingProps {
  onStart: () => void;
  remainingInterviews?: number;
  isFreeUser?: boolean;
  bonusCredits?: number;
}

const InterviewOnboarding: React.FC<InterviewOnboardingProps> = ({
  onStart,
  remainingInterviews,
  isFreeUser,
  bonusCredits,
}) => {
  return (
    <div className="text-center">
      {/* Free user info */}
      {isFreeUser && remainingInterviews !== undefined && remainingInterviews !== Infinity && (
        <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6 max-w-lg mx-auto">
          <p className="text-purple-700 dark:text-purple-300 font-medium">
            Free tier: {remainingInterviews} interview{remainingInterviews !== 1 ? 's' : ''} remaining today
            {bonusCredits && bonusCredits > 0 ? ` (+${bonusCredits} bonus)` : ''}
          </p>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8 text-purple-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Interview Practice
        </h1>
        <p className="text-gray-600 max-w-md mx-auto">
          Practice your job interview skills with AI-powered questions and get feedback on both your answers and pronunciation.
        </p>
      </div>

      {/* Steps */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-8 max-w-lg mx-auto">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          How it works
        </h2>
        <div className="space-y-4 text-left">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 font-semibold text-sm">1</span>
            </div>
            <div>
              <h3 className="font-medium text-gray-800">Tell us about the job</h3>
              <p className="text-sm text-gray-600">
                Chat with our AI to describe what position you're interviewing for.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 font-semibold text-sm">2</span>
            </div>
            <div>
              <h3 className="font-medium text-gray-800">Answer interview questions</h3>
              <p className="text-sm text-gray-600">
                We'll generate 3-5 relevant questions based on your target role.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 font-semibold text-sm">3</span>
            </div>
            <div>
              <h3 className="font-medium text-gray-800">Record your answers</h3>
              <p className="text-sm text-gray-600">
                Speak your answers out loud - we'll transcribe and analyze them.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
              <span className="text-purple-600 font-semibold text-sm">4</span>
            </div>
            <div>
              <h3 className="font-medium text-gray-800">Get detailed feedback</h3>
              <p className="text-sm text-gray-600">
                Receive feedback on your pronunciation and answer quality.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className="bg-purple-50 rounded-lg p-4 mb-8 max-w-lg mx-auto">
        <p className="text-purple-700 text-sm">
          <span className="font-semibold">Tip:</span> Find a quiet place and speak clearly.
          The AI will evaluate both what you say and how you say it.
        </p>
      </div>

      {/* Start button */}
      <button
        onClick={onStart}
        className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
      >
        Start Interview Practice
      </button>
    </div>
  );
};

export default InterviewOnboarding;
